import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { 
  Plus, 
  CircleNotch,
  Trash,
  PencilSimple,
  ShieldCheck,
  Warning,
  XCircle,
  CheckCircle,
  Calendar,
  Buildings,
  User,
  Certificate,
  FileText,
  CaretDown,
  Funnel,
  ArrowsClockwise,
  Bell,
  CloudArrowUp,
  File,
  DownloadSimple,
  Eye,
  Paperclip
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'license', label: 'License', icon: Certificate },
  { value: 'visa', label: 'Visa', icon: User },
  { value: 'insurance', label: 'Insurance', icon: ShieldCheck },
  { value: 'vehicle', label: 'Vehicle', icon: Buildings },
  { value: 'contract', label: 'Contract', icon: FileText },
  { value: 'permit', label: 'Permit', icon: Certificate },
  { value: 'certification', label: 'Certification', icon: Certificate },
  { value: 'other', label: 'Other', icon: FileText },
];

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20', icon: CheckCircle },
  expiring_soon: { label: 'Expiring Soon', color: 'bg-amber-500/10 text-amber-600 border-amber-500/20', icon: Warning },
  expired: { label: 'Expired', color: 'bg-red-500/10 text-red-600 border-red-500/20', icon: XCircle },
};

export default function CompliancePage() {
  const { user, hasPermission } = useAuth();
  const { formatCurrency } = useCompany();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadItemId, setUploadItemId] = useState(null);
  const [viewDocsItem, setViewDocsItem] = useState(null);
  const fileInputRef = useRef(null);
  
  // Filters
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    category: 'license',
    entity_type: 'company',
    entity_id: '',
    start_date: new Date().toISOString().split('T')[0],
    expiry_date: '',
    reminder_days: [30, 7, 1],
    description: '',
    reference_number: ''
  });

  const canCreate = hasPermission('hr', 'create');
  const canEdit = hasPermission('hr', 'edit');
  const canDelete = hasPermission('hr', 'delete');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, itemsRes, employeesRes] = await Promise.all([
        axios.get(`${API_URL}/api/compliance/stats`),
        axios.get(`${API_URL}/api/compliance/items`),
        axios.get(`${API_URL}/api/hr/employees`)
      ]);
      setStats(statsRes.data);
      setItems(itemsRes.data);
      setEmployees(employeesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch compliance data:', error);
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.expiry_date) {
      toast.error('Title and expiry date are required');
      return;
    }

    setFormLoading(true);
    try {
      if (editItem) {
        await axios.patch(`${API_URL}/api/compliance/items/${editItem.id}`, formData);
        toast.success('Compliance item updated');
      } else {
        await axios.post(`${API_URL}/api/compliance/items`, formData);
        toast.success('Compliance item created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save compliance item');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this compliance item?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/compliance/items/${id}`);
      toast.success('Compliance item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete compliance item');
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      title: item.title,
      category: item.category,
      entity_type: item.entity_type,
      entity_id: item.entity_id || '',
      start_date: item.start_date,
      expiry_date: item.expiry_date,
      reminder_days: item.reminder_days || [30, 7, 1],
      description: item.description || '',
      reference_number: item.reference_number || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditItem(null);
    setFormData({
      title: '',
      category: 'license',
      entity_type: 'company',
      entity_id: '',
      start_date: new Date().toISOString().split('T')[0],
      expiry_date: '',
      reminder_days: [30, 7, 1],
      description: '',
      reference_number: ''
    });
  };

  const handleSyncVisas = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API_URL}/api/compliance/sync-employee-visas`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to sync employee visas');
    } finally {
      setSyncing(false);
    }
  };

  const handleCheckAlerts = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/compliance/check-alerts`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to check alerts');
    }
  };

  const handleUploadClick = (itemId) => {
    setUploadItemId(itemId);
    setUploadDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadItemId) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    const allowedExt = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.gif', '.webp'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedExt.includes(ext)) {
      toast.error(`File type not allowed. Allowed: ${allowedExt.join(', ')}`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API_URL}/api/compliance/items/${uploadItemId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      setUploadItemId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (itemId, docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await axios.delete(`${API_URL}/api/compliance/items/${itemId}/documents/${docId}`);
      toast.success('Document deleted');
      fetchData();
      // Update viewDocsItem if viewing
      if (viewDocsItem?.id === itemId) {
        const item = items.find(i => i.id === itemId);
        if (item) {
          setViewDocsItem({
            ...item,
            documents: (item.documents || []).filter(d => d.id !== docId)
          });
        }
      }
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleViewDocuments = (item) => {
    setViewDocsItem(item);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const filteredItems = items.filter(item => {
    if (filterCategory !== 'all' && item.category !== filterCategory) return false;
    if (filterStatus !== 'all' && item.status !== filterStatus) return false;
    if (searchTerm && !item.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat?.icon || FileText;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="compliance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <ShieldCheck size={28} weight="duotone" className="text-primary" />
            Compliance Center
          </h1>
          <p className="text-muted-foreground mt-1">Track licenses, visas, insurance, and other compliance items</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSyncVisas} disabled={syncing} data-testid="sync-visas-btn">
            {syncing ? <CircleNotch className="animate-spin mr-2" size={16} /> : <ArrowsClockwise className="mr-2" size={16} />}
            Sync Visas
          </Button>
          <Button variant="outline" onClick={handleCheckAlerts} data-testid="check-alerts-btn">
            <Bell className="mr-2" size={16} />
            Check Alerts
          </Button>
          {canCreate && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-compliance-btn">
                  <Plus size={16} className="mr-2" />
                  Add Item
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editItem ? 'Edit Compliance Item' : 'Add Compliance Item'}</DialogTitle>
                  <DialogDescription>
                    Track licenses, visas, insurance, and other compliance items
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label>Title *</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                        placeholder="e.g., Trade License 2024"
                        data-testid="compliance-title"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select value={formData.category} onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}>
                        <SelectTrigger data-testid="compliance-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Related To</Label>
                      <Select value={formData.entity_type} onValueChange={(v) => setFormData(p => ({ ...p, entity_type: v, entity_id: '' }))}>
                        <SelectTrigger data-testid="compliance-entity-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="company">Company</SelectItem>
                          <SelectItem value="employee">Employee</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {formData.entity_type === 'employee' && (
                      <div className="col-span-2 space-y-2">
                        <Label>Employee</Label>
                        <Select value={formData.entity_id} onValueChange={(v) => setFormData(p => ({ ...p, entity_id: v }))}>
                          <SelectTrigger data-testid="compliance-employee">
                            <SelectValue placeholder="Select employee" />
                          </SelectTrigger>
                          <SelectContent>
                            {employees.map(emp => (
                              <SelectItem key={emp.id} value={emp.id}>
                                {emp.first_name} {emp.last_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData(p => ({ ...p, start_date: e.target.value }))}
                        data-testid="compliance-start-date"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Expiry Date *</Label>
                      <Input
                        type="date"
                        value={formData.expiry_date}
                        onChange={(e) => setFormData(p => ({ ...p, expiry_date: e.target.value }))}
                        data-testid="compliance-expiry-date"
                      />
                    </div>
                    
                    <div className="col-span-2 space-y-2">
                      <Label>Reference Number</Label>
                      <Input
                        value={formData.reference_number}
                        onChange={(e) => setFormData(p => ({ ...p, reference_number: e.target.value }))}
                        placeholder="License/Document number"
                        data-testid="compliance-reference"
                      />
                    </div>
                    
                    <div className="col-span-2 space-y-2">
                      <Label>Reminder Days (comma separated)</Label>
                      <Input
                        value={formData.reminder_days.join(', ')}
                        onChange={(e) => {
                          const days = e.target.value.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d));
                          setFormData(p => ({ ...p, reminder_days: days.length > 0 ? days : [30, 7, 1] }));
                        }}
                        placeholder="30, 7, 1"
                        data-testid="compliance-reminders"
                      />
                    </div>
                    
                    <div className="col-span-2 space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                        placeholder="Additional notes..."
                        rows={2}
                        data-testid="compliance-description"
                      />
                    </div>
                  </div>
                  
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" disabled={formLoading} data-testid="save-compliance-btn">
                      {formLoading && <CircleNotch className="animate-spin mr-2" size={16} />}
                      {editItem ? 'Update' : 'Create'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-tactile rounded-lg p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Items</p>
              <p className="text-2xl font-bold mt-1">{stats?.total || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-primary/10">
              <ShieldCheck size={24} className="text-primary" weight="duotone" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-tactile rounded-lg p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Active</p>
              <p className="text-2xl font-bold mt-1 text-emerald-600">{stats?.active || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-emerald-500/10">
              <CheckCircle size={24} className="text-emerald-600" weight="duotone" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-tactile rounded-lg p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Expiring Soon</p>
              <p className="text-2xl font-bold mt-1 text-amber-600">{stats?.expiring_soon || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-amber-500/10">
              <Warning size={24} className="text-amber-600" weight="duotone" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card-tactile rounded-lg p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Expired</p>
              <p className="text-2xl font-bold mt-1 text-red-600">{stats?.expired || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-red-500/10">
              <XCircle size={24} className="text-red-600" weight="duotone" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Upcoming Expirations Alert */}
      {stats?.upcoming_expirations?.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card-tactile rounded-lg p-5 border-l-4 border-amber-500"
        >
          <div className="flex items-center gap-2 mb-3">
            <Warning size={20} className="text-amber-600" />
            <h3 className="font-medium">Upcoming Expirations</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {stats.upcoming_expirations.slice(0, 5).map(item => (
              <Badge 
                key={item.id} 
                variant="outline" 
                className={STATUS_CONFIG[item.status]?.color}
              >
                {item.title} - {item.days_until_expiry < 0 ? 'Expired' : `${item.days_until_expiry} days`}
              </Badge>
            ))}
            {stats.upcoming_expirations.length > 5 && (
              <Badge variant="outline">+{stats.upcoming_expirations.length - 5} more</Badge>
            )}
          </div>
        </motion.div>
      )}

      {/* Filters and Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card-tactile rounded-lg overflow-hidden"
      >
        {/* Filters */}
        <div className="p-4 border-b border-border bg-muted/30">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Input
                placeholder="Search compliance items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
                data-testid="compliance-search"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-40" data-testid="filter-category">
                  <Funnel size={14} className="mr-2" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="compliance-table">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-sm">Item</th>
                <th className="text-left px-4 py-3 font-medium text-sm">Category</th>
                <th className="text-left px-4 py-3 font-medium text-sm">Related To</th>
                <th className="text-left px-4 py-3 font-medium text-sm">Expiry Date</th>
                <th className="text-left px-4 py-3 font-medium text-sm">Status</th>
                <th className="text-right px-4 py-3 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    No compliance items found
                  </td>
                </tr>
              ) : (
                filteredItems.map(item => {
                  const StatusIcon = STATUS_CONFIG[item.status]?.icon || CheckCircle;
                  const CategoryIcon = getCategoryIcon(item.category);
                  
                  return (
                    <tr key={item.id} className="hover:bg-muted/30 transition-colors" data-testid={`compliance-row-${item.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-muted">
                            <CategoryIcon size={18} className="text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium">{item.title}</p>
                            {item.reference_number && (
                              <p className="text-xs text-muted-foreground">#{item.reference_number}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="capitalize">
                          {item.category}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {item.entity_type === 'employee' ? (
                            <User size={14} className="text-muted-foreground" />
                          ) : (
                            <Buildings size={14} className="text-muted-foreground" />
                          )}
                          <span className="text-sm">{item.entity_name || 'Company'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Calendar size={14} className="text-muted-foreground" />
                          <span className="text-sm">{item.expiry_date}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {item.days_until_expiry < 0 
                            ? `${Math.abs(item.days_until_expiry)} days overdue`
                            : item.days_until_expiry === 0 
                            ? 'Expires today'
                            : `${item.days_until_expiry} days left`
                          }
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={STATUS_CONFIG[item.status]?.color}>
                          <StatusIcon size={14} className="mr-1" />
                          {STATUS_CONFIG[item.status]?.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          {/* Document count badge */}
                          {(item.documents?.length > 0) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleViewDocuments(item)}
                              className="relative"
                              title="View documents"
                              data-testid={`docs-${item.id}`}
                            >
                              <Paperclip size={16} />
                              <span className="absolute -top-1 -right-1 text-[10px] bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center">
                                {item.documents.length}
                              </span>
                            </Button>
                          )}
                          {canCreate && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleUploadClick(item.id)}
                              title="Upload document"
                              data-testid={`upload-${item.id}`}
                            >
                              <CloudArrowUp size={16} />
                            </Button>
                          )}
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(item)}
                              data-testid={`edit-${item.id}`}
                            >
                              <PencilSimple size={16} />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(item.id)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-${item.id}`}
                            >
                              <Trash size={16} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Category Summary */}
        {stats?.by_category && Object.keys(stats.by_category).length > 0 && (
          <div className="p-4 border-t border-border bg-muted/30">
            <p className="text-sm font-medium mb-3">By Category</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_category).map(([category, counts]) => (
                <div key={category} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-background border border-border">
                  <span className="text-sm font-medium capitalize">{category}</span>
                  <div className="flex gap-1">
                    <Badge variant="outline" className="text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                      {counts.active}
                    </Badge>
                    {counts.expiring_soon > 0 && (
                      <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/20">
                        {counts.expiring_soon}
                      </Badge>
                    )}
                    {counts.expired > 0 && (
                      <Badge variant="outline" className="text-xs bg-red-500/10 text-red-600 border-red-500/20">
                        {counts.expired}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={(open) => { setUploadDialogOpen(open); if (!open) setUploadItemId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Upload a document for this compliance item (PDF, DOC, DOCX, JPG, PNG - Max 10MB)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div 
              className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
              />
              <CloudArrowUp size={40} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">Click to select a file</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
              Cancel
            </Button>
            {uploading && (
              <Button disabled>
                <CircleNotch className="animate-spin mr-2" size={16} />
                Uploading...
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Documents Dialog */}
      {viewDocsItem && (
        <Dialog open={!!viewDocsItem} onOpenChange={() => setViewDocsItem(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Documents - {viewDocsItem.title}</DialogTitle>
              <DialogDescription>
                {viewDocsItem.documents?.length || 0} document(s) attached
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {(!viewDocsItem.documents || viewDocsItem.documents.length === 0) ? (
                <p className="text-center text-muted-foreground py-8">No documents uploaded yet</p>
              ) : (
                viewDocsItem.documents.map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30">
                    <div className="p-2 rounded-lg bg-background">
                      <File size={20} className="text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{doc.filename}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(doc.file_size)} • {new Date(doc.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => window.open(`${API_URL}${doc.file_url}`, '_blank')}
                        title="Download"
                      >
                        <DownloadSimple size={16} />
                      </Button>
                      {doc.mime_type?.startsWith('image/') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => window.open(`${API_URL}${doc.file_url}`, '_blank')}
                          title="Preview"
                        >
                          <Eye size={16} />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteDocument(viewDocsItem.id, doc.id)}
                          className="text-destructive hover:text-destructive"
                          title="Delete"
                        >
                          <Trash size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewDocsItem(null)}>Close</Button>
              {canCreate && (
                <Button onClick={() => { setViewDocsItem(null); handleUploadClick(viewDocsItem.id); }}>
                  <CloudArrowUp size={16} className="mr-2" />
                  Upload More
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
